import React, { useState, useEffect } from 'react';
import '../css/OrderedCars.css';
import '../css/HighwayTicketButton.css';
import {FaEye, FaCheckCircle, FaTimesCircle, FaChevronRight, FaRoad } from 'react-icons/fa';
import CarDropdown from '../modal/CarDropdown';
import OrderedCarDropdown from '../modal/OrderedCarDropdown';
import { authAPI, vehiclesAPI, highwayTicketAPI } from '../api/api';
import { usePermissions } from '../hooks/usePermissions';



function FilterModal({ open, onClose, fields, visibleFields, onChange }) {
  if (!open) return null;
  return (
    <div className="field-filter-modal-backdrop" onClick={onClose}>
      <div className="field-filter-modal-content" onClick={e => e.stopPropagation()}>
        <h3>Mezők szűrése</h3>
        <form>
          {fields.map(field => (
            <div key={field.key}>
              <label>
                <input
                  type="checkbox"
                  checked={visibleFields.includes(field.key)}
                  onChange={() => onChange(field.key)}
                />
                {field.label}
              </label>
            </div>
          ))}
        </form>
        <button onClick={onClose} className="field-filter-modal-close-btn">Bezár</button>
      </div>
    </div>
  );
}

function CarRow({ car, visibleFields, isOpen, onDropdown, onHighwayTicketRequest, requestingTicket, showClientBadge }) {
  // Calculate progress based on IVS status
  let progress = 0;
  let progressColor = 'gray';
  let statusLabel = 'Megrendelve';

  if (car.ivs_progress && car.ivs_progress.steps) {
    const completedSteps = car.ivs_progress.steps.filter(step => step.done).length;
    const totalSteps = car.ivs_progress.steps.length;
    progress = (completedSteps / totalSteps) * 100;

    // Determine color based on progress
    if (progress === 100) {
      progressColor = 'green';
      statusLabel = 'Átadva';
    } else if (progress >= 75) {
      progressColor = 'blue';
      statusLabel = 'Szállítás alatt';
    } else if (progress >= 50) {
      progressColor = 'orange';
      statusLabel = 'Gyártás alatt';
    } else if (progress >= 25) {
      progressColor = 'yellow';
      statusLabel = 'Megrendelve';
    }
  } else {
    // Default for cars without IVS data
    progress = 25;
    progressColor = 'yellow';
    statusLabel = 'Megrendelve';
  }

  let statusText = car.statusText;
  let dynamicStatus = car.status_info?.status || car.status;

  const [shouldRenderDropdown, setShouldRenderDropdown] = useState(isOpen);
  useEffect(() => {
    if (isOpen) {
      setShouldRenderDropdown(true);
    } else if (shouldRenderDropdown) {
      const timeout = setTimeout(() => setShouldRenderDropdown(false), 450);
      return () => clearTimeout(timeout);
    }
  }, [isOpen, shouldRenderDropdown]);

  return (
    <>
      <div className="car-card" style={{display:'flex',flexDirection:'row',position:'relative'}}>
        <div className="car-card-imgwrap">
          <img src={car.gallery && car.gallery[0]} alt={car.model} className="car-card-img" />
        </div>
        <div style={{flex:1,display:'flex',flexDirection:'column'}}>
          <div className="car-card-row1">
            {visibleFields.includes('chassis_number') && (
              <div className="car-card-cell vin" style={{display:'flex',flexDirection:'column',alignItems:'center'}}>
                <span className='cell_span'>Alvázszám</span>
                {car.chassis_number17}
              </div>
            )}
            {visibleFields.includes('plate_number') && (
              <div className="car-card-cell plate" style={{display:'flex',flexDirection:'column',alignItems:'center'}}>
                <span className='cell_span'>Rendszám</span>
                {car.plate_number}
              </div>
            )}
            {visibleFields.includes('handover') && (
              <div className="car-card-cell handover" style={{display:'flex',flexDirection:'column',alignItems:'center'}}>
                <span className='cell_span'>Átadás</span>
                {car.handover}
              </div>
            )}
            {visibleFields.includes('rentalFee') && (
              <div className="car-card-cell rentalFee" style={{display:'flex',flexDirection:'column',alignItems:'center'}}>
                <span className='cell_span'>Bérleti díj (/hó)</span>
                {car.rentalFee || '-'}
              </div>
            )}
          </div>
          <div className="car-row-divider" style={{marginLeft:0,width:'100%'}}></div>
          <div className="car-card-row2">
            <div className="car-card-badges">
              {showClientBadge && car.company !== '---' && (
                <span className="car-card-badge client">{car.company}</span>
              )}
              <span className="car-card-badge owner">{car.owner}</span>
              <span className="car-card-badge highway">Autópálya matrica: {car.highway_ticket}</span>
              {(!car.highway_ticket || car.highway_ticket === '---' || car.highway_ticket === 'Nincs' || car.highway_ticket === '') && (
                <button
                  className="highway-ticket-request-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onHighwayTicketRequest(car.plate_number);
                  }}
                  title="Autópálya matrica igénylése"
                  disabled={requestingTicket === car.plate_number}
                >
                  <FaRoad /> {requestingTicket === car.plate_number ? 'Igénylés...' : 'Matrica igénylés'}
                </button>
              )}
            </div>
            <div className="car-card-progresswrap">
              <div className="car-card-progress-bg">
                <div className={`car-card-progress-bar car-card-progress-bar-${progressColor}`} style={{ width: `${progress}%` }}></div>
              </div>
            </div>
          </div>
        </div>
        <div className="car-card-dropdownicon" style={{position:'absolute',right:5, top:5}} onClick={() => onDropdown(car.chassis_number)}>
          <FaChevronRight className="car-dropdown-icon" style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }} />
        </div>
      </div>
      <div className={`car-dropdown-anim-wrapper${isOpen ? ' open' : ''}`}>{shouldRenderDropdown && <OrderedCarDropdown car={car} />}</div>
    </>
  );
}

const OrderedCars = ({ search, sort, selectedClientId = null }) => {
  const { permissions, loading: permissionsLoading } = usePermissions();

  const allFields = [
    { key: 'chassis_number', label: 'Alvázszám' },
    { key: 'plate_number', label: 'Rendszám' },
    { key: 'handover', label: 'Átadás' },
    ...(permissions?.can_see_rental_fee ? [{ key: 'rentalFee', label: 'Bérköltség' }] : []),
  ];

  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [visibleFields, setVisibleFields] = useState([]);
  const [cars, setCars] = useState([]);
  const [openDropdowns, setOpenDropdowns] = useState([]);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [requestingTicket, setRequestingTicket] = useState(null);

  const user = authAPI.getCurrentUser();
  // Több cég egyidejű listázásakor jelöljük, melyik autó melyik céghez tartozik
  const showClientBadge = !selectedClientId && authAPI.getAccessibleClients().length > 1;

  // Initialize visible fields when permissions are loaded
  useEffect(() => {
    // Wait until permissions are fully loaded
    if (permissionsLoading) {
      return;
    }

    // Build the current allFields based on permissions
    const currentFields = [
      { key: 'chassis_number', label: 'Alvázszám' },
      { key: 'plate_number', label: 'Rendszám' },
      { key: 'handover', label: 'Átadás' },
      ...(permissions?.can_see_rental_fee ? [{ key: 'rentalFee', label: 'Bérköltség' }] : []),
    ];

    // Try to load saved visible fields from localStorage
    const savedFields = localStorage.getItem('orderedCars_visibleFields');

    if (savedFields) {
      try {
        const parsed = JSON.parse(savedFields);

        // Filter to only valid fields
        let validFields = parsed.filter(key => currentFields.some(f => f.key === key));

        // If user has rentalFee permission and it's not in saved fields, add it
        if (permissions?.can_see_rental_fee && !validFields.includes('rentalFee')) {
          validFields.push('rentalFee');
        }

        if (validFields.length > 0) {
          setVisibleFields(validFields);
          return;
        }
      } catch (e) {
        console.error('Error loading saved fields:', e);
      }
    }

    // Default: show all fields (including rentalFee if user has permission)
    const defaultFields = currentFields.map(f => f.key);
    setVisibleFields(defaultFields);
  }, [permissionsLoading, permissions?.can_see_rental_fee]);

  // Save visible fields to localStorage whenever they change
  useEffect(() => {
    if (!permissionsLoading && visibleFields.length > 0) {
      localStorage.setItem('orderedCars_visibleFields', JSON.stringify(visibleFields));
    }
  }, [visibleFields, permissionsLoading]);

  const handleHighwayTicketRequest = async (plateNumber) => {
    if (requestingTicket === plateNumber) return;

    setRequestingTicket(plateNumber);

    try {
      await highwayTicketAPI.requestTicket(plateNumber);
      alert('Autópálya matrica igénylése sikeresen elküldve!');

      // Update the car's highway_ticket locally to hide the button immediately
      setCars(prevCars => prevCars.map(car =>
        car.plate_number === plateNumber
          ? { ...car, highway_ticket: 'Igényelve' }
          : car
      ));
    } catch (error) {
      console.error('Highway ticket request error:', error);
      alert('Hiba történt az autópálya matrica igénylésekor: ' + error.message);
    } finally {
      setRequestingTicket(null);
    }
  };

  useEffect(() => {
    const loadCars = async () => {
      try {
        if (!user) {
          console.warn('Nincs bejelentkezett felhasználó');
          setCars([]);
          return;
        }

        const carsData = await vehiclesAPI.getAllVehiclesForUser();
        setCars(carsData);
      } catch (error) {
        console.error('Hiba a járművek betöltésekor:', error);
        setCars([]);
      }
    };

    loadCars();
  }, [user?.id, selectedClientId]);

  useEffect(() => {
    document.body.classList.add('cars-bg');
    return () => document.body.classList.remove('cars-bg');
  }, []);

  let filteredCars = cars;

  let carsToShow = filteredCars.filter(car => car.status_info?.status === 'order');
  if (search) {
    const s = search.toLowerCase();
    carsToShow = carsToShow.filter(car =>
      car.chassis_number.toLowerCase().includes(s) ||
      car.chassis_number17.toLowerCase().includes(s) ||
      car.plate_number.toLowerCase().includes(s) ||
      car.model.toLowerCase().includes(s) ||
      car.owner.toLowerCase().includes(s)
    );
  }
  if (sortField) {
    carsToShow = [...carsToShow].sort((a, b) => {
      let aVal = a[sortField] || '';
      let bVal = b[sortField] || '';
      if (sortField === 'rentalFee') {
        aVal = parseInt((aVal || '').replace(/[^0-9]/g, '')) || 0;
        bVal = parseInt((bVal || '').replace(/[^0-9]/g, '')) || 0;
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }

  const handleFieldToggle = (key) => {
    setVisibleFields(prev =>
      prev.includes(key)
        ? prev.filter(f => f !== key)
        : [...prev, key]
    );
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(dir => dir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  return (
    <>
      <div className="home-table-header-row">
          <div className="home-table-header" style={{ cursor: 'pointer' }} onClick={() => setFilterModalOpen(true)}>
            <FaEye />
          </div>
        {allFields.map((field) => (
          visibleFields.includes(field.key) && (
            <div
              className="home-table-header-cell"
              key={field.key}
              onClick={() => handleSort(field.key)}
              style={{ cursor: 'pointer', userSelect: 'none', fontWeight: sortField === field.key ? 'bold' : undefined }}
            >
              {field.label}
              {sortField === field.key && (
                <span style={{ marginLeft: 4 }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
              )}
            </div>
          )
        ))}
      </div>
      {carsToShow.map((car, idx) => (
        <CarRow
          key={idx}
          car={car}
          visibleFields={visibleFields}
          isOpen={openDropdowns.includes(car.chassis_number)}
          onDropdown={chassis_number => setOpenDropdowns(openDropdowns => openDropdowns.includes(chassis_number) ? openDropdowns.filter(v => v !== chassis_number) : [...openDropdowns, chassis_number])}
          onHighwayTicketRequest={handleHighwayTicketRequest}
          requestingTicket={requestingTicket}
          showClientBadge={showClientBadge}
        />
      ))}
      <FilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        fields={allFields}
        visibleFields={visibleFields}
        onChange={handleFieldToggle}
      />
    </>
  );
};

export default OrderedCars;